<template>
  <div class="profile-page">
    <header class="header">
      <h1 class="title">个人中心</h1>
    </header>

    <main class="content">
      <el-card class="user-card" shadow="never">
        <div class="user-row">
          <el-avatar :size="72" class="user-avatar">
            {{ userInitial }}
          </el-avatar>
          <div class="user-meta">
            <div class="user-name">{{ displayName }}</div>
            <div class="user-account">账号：{{ account || '未登录' }}</div>
          </div>
        </div>
      </el-card>

      <el-card class="menu-card" shadow="never" :body-style="{ padding: 0 }">
        <div
          v-for="item in menuItems"
          :key="item.path"
          class="menu-item"
          role="button"
          tabindex="0"
          @click="go(item.path)"
          @keydown.enter="go(item.path)"
        >
          <div class="menu-left">
            <el-icon class="menu-icon" :style="{ color: item.color }">
              <component :is="item.icon" />
            </el-icon>
            <span class="menu-label">{{ item.label }}</span>
          </div>
          <el-icon class="menu-arrow"><ArrowRight /></el-icon>
        </div>
      </el-card>

      <el-button
        class="logout-btn"
        type="danger"
        size="large"
        :loading="loggingOut"
        @click="logout"
      >
        退出登录
      </el-button>
    </main>

    <nav class="tab-bar" aria-label="底部导航">
      <div class="tab-item" @click="go('/home')">
        <img src="@/assets/首页.svg" alt="" class="tab-icon" />
        <span class="tab-text">首页</span>
      </div>
      <div class="tab-item" @click="go('/dashboard')">
        <img src="@/assets/看板.svg" alt="" class="tab-icon" />
        <span class="tab-text">看板</span>
      </div>
      <div class="tab-item active" @click="go('/profile')">
        <img src="@/assets/我的.svg" alt="" class="tab-icon" />
        <span class="tab-text">我的</span>
      </div>
    </nav>
  </div>
</template>

<script>
import { ref, computed, onMounted, markRaw } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import {
  ArrowRight,
  Lock,
  Setting,
  Document,
  InfoFilled
} from '@element-plus/icons-vue'
import { getUicAuthUserInfo, clearUicAuthStorage } from '@/utils/auth.js'

export default {
  name: 'ProfileView',
  components: {
    ArrowRight,
    Lock,
    Setting,
    Document,
    InfoFilled
  },
  setup() {
    const router = useRouter()
    const username = ref('')
    const account = ref('')
    const loggingOut = ref(false)

    const menuItems = [
      {
        label: '修改密码',
        path: '/change-password',
        icon: markRaw(Lock),
        color: 'var(--primary-color)'
      },
      {
        label: '系统设置',
        path: '/system-settings',
        icon: markRaw(Setting),
        color: '#9f7aea'
      },
      {
        label: '版本更新记录',
        path: '/update-log',
        icon: markRaw(Document),
        color: '#f39c12'
      },
      {
        label: '关于我们',
        path: '/about-us',
        icon: markRaw(InfoFilled),
        color: '#38b2ac'
      }
    ]

    const displayName = computed(() => username.value || '未知用户')

    const userInitial = computed(() => {
      const name = username.value || account.value || 'U'
      return String(name).charAt(0).toUpperCase()
    })

    onMounted(() => {
      const userInfo = getUicAuthUserInfo()
      if (userInfo) {
        username.value =
          userInfo.nickName || userInfo.username || '未知用户'
        account.value = userInfo.username || ''
      }
    })

    const go = (path) => {
      if (!path) return
      router.push(path)
    }

    const logout = async () => {
      if (loggingOut.value) return
      try {
        await ElMessageBox.confirm('确定退出当前账号吗？', '退出登录', {
          confirmButtonText: '退出',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        return
      }
      loggingOut.value = true
      try {
        clearUicAuthStorage()
        ElMessage.success('已退出登录')
        router.replace('/login')
      } finally {
        loggingOut.value = false
      }
    }

    return {
      username,
      account,
      displayName,
      userInitial,
      menuItems,
      loggingOut,
      go,
      logout
    }
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: var(--background-color);
  padding-bottom: calc(60px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.content {
  padding: 12px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.user-card,
.menu-card {
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.user-card :deep(.el-card__body) {
  padding: 16px;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.user-avatar {
  flex: 0 0 auto;
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  font-size: 28px;
  font-weight: 600;
}

.user-meta {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
  word-break: break-all;
}

.user-account {
  margin-top: 4px;
  font-size: 13px;
  color: var(--text-secondary);
  word-break: break-all;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:active {
  background: rgba(0, 0, 0, 0.04);
}

.menu-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.menu-icon {
  font-size: 18px;
}

.menu-label {
  font-size: 15px;
  color: var(--text-primary);
}

.menu-arrow {
  color: var(--text-light, #c0c4cc);
  font-size: 14px;
}

.logout-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}

.tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: calc(60px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  background: var(--card-background);
  box-shadow: 0 -2px 10px var(--shadow-light);
  border-top: 1px solid var(--border-color);
  z-index: 1000;
  box-sizing: border-box;
}

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  cursor: pointer;
}

.tab-icon {
  width: 28px;
  height: 28px;
  margin-bottom: 2px;
  opacity: 0.5;
}

.tab-text {
  font-size: 12px;
  color: var(--text-light, #909399);
}

.tab-item.active .tab-icon {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(46%) sepia(79%) saturate(333%)
    hue-rotate(142deg) brightness(91%) contrast(89%);
}

.tab-item.active .tab-text {
  color: var(--primary-color);
  font-weight: 500;
}
</style>
